'use client'

import { useState } from 'react'
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps'
import { geoAlbersUsa, geoCentroid, geoPath } from 'd3-geo'
import statesData from '@/app/data/states_data.json'
import type { CityDatum } from '@/app/markets/page'

type HeatmapMapProps = {
  cities: CityDatum[]
  selectedState?: string | null
  onStateSelect: (state: string | null) => void
  onCitySelect: (city: CityDatum) => void
}

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json'
const MAP_WIDTH = 800
const MAP_HEIGHT = 600

const STATE_NAME_TO_ABBR: Record<string, string> = {
  ALABAMA: 'AL',
  ALASKA: 'AK',
  ARIZONA: 'AZ',
  ARKANSAS: 'AR',
  CALIFORNIA: 'CA',
  COLORADO: 'CO',
  CONNECTICUT: 'CT',
  DELAWARE: 'DE',
  FLORIDA: 'FL',
  GEORGIA: 'GA',
  HAWAII: 'HI',
  IDAHO: 'ID',
  ILLINOIS: 'IL',
  INDIANA: 'IN',
  IOWA: 'IA',
  KANSAS: 'KS',
  KENTUCKY: 'KY',
  LOUISIANA: 'LA',
  MAINE: 'ME',
  MARYLAND: 'MD',
  MASSACHUSETTS: 'MA',
  MICHIGAN: 'MI',
  MINNESOTA: 'MN',
  MISSISSIPPI: 'MS',
  MISSOURI: 'MO',
  MONTANA: 'MT',
  NEBRASKA: 'NE',
  NEVADA: 'NV',
  NEW_HAMPSHIRE: 'NH',
  NEW_JERSEY: 'NJ',
  NEW_MEXICO: 'NM',
  NEW_YORK: 'NY',
  NORTH_CAROLINA: 'NC',
  NORTH_DAKOTA: 'ND',
  OHIO: 'OH',
  OKLAHOMA: 'OK',
  OREGON: 'OR',
  PENNSYLVANIA: 'PA',
  RHODE_ISLAND: 'RI',
  SOUTH_CAROLINA: 'SC',
  SOUTH_DAKOTA: 'SD',
  TENNESSEE: 'TN',
  TEXAS: 'TX',
  UTAH: 'UT',
  VERMONT: 'VT',
  VIRGINIA: 'VA',
  WASHINGTON: 'WA',
  WEST_VIRGINIA: 'WV',
  WISCONSIN: 'WI',
  WYOMING: 'WY',
  'DISTRICT_OF_COLUMBIA': 'DC',
  'WASHINGTON_DC': 'DC',
  'WASHINGTON, DC': 'DC'
}

const FIPS_TO_ABBR: Record<string, string> = {
  '01': 'AL',
  '02': 'AK',
  '04': 'AZ',
  '05': 'AR',
  '06': 'CA',
  '08': 'CO',
  '09': 'CT',
  '10': 'DE',
  '11': 'DC',
  '12': 'FL',
  '13': 'GA',
  '15': 'HI',
  '16': 'ID',
  '17': 'IL',
  '18': 'IN',
  '19': 'IA',
  '20': 'KS',
  '21': 'KY',
  '22': 'LA',
  '23': 'ME',
  '24': 'MD',
  '25': 'MA',
  '26': 'MI',
  '27': 'MN',
  '28': 'MS',
  '29': 'MO',
  '30': 'MT',
  '31': 'NE',
  '32': 'NV',
  '33': 'NH',
  '34': 'NJ',
  '35': 'NM',
  '36': 'NY',
  '37': 'NC',
  '38': 'ND',
  '39': 'OH',
  '40': 'OK',
  '41': 'OR',
  '42': 'PA',
  '44': 'RI',
  '45': 'SC',
  '46': 'SD',
  '47': 'TN',
  '48': 'TX',
  '49': 'UT',
  '50': 'VT',
  '51': 'VA',
  '53': 'WA',
  '54': 'WV',
  '55': 'WI',
  '56': 'WY'
}

const stateNameToAbbr = Object.entries(statesData).reduce(
  (acc, [abbr, info]) => {
    const name = typeof info?.name === 'string' ? info.name.trim().toUpperCase() : ''
    if (name) {
      acc[name] = abbr
    }
    return acc
  },
  {} as Record<string, string>
)

const getStateCode = (geo: any) => {
  const props = geo?.properties ?? {}
  const candidate =
    props.postal ?? props.abbrev ?? props.abbr ?? props.code ?? props.iso_3166_2
  if (candidate) return String(candidate).trim().toUpperCase()
  const fips =
    props.id ?? props.fips ?? props.STATEFP ?? props.statefp ?? geo?.id ?? geo?.properties?.id
  if (fips != null) {
    const key = String(fips).padStart(2, '0')
    if (FIPS_TO_ABBR[key]) return FIPS_TO_ABBR[key]
  }
  const name = props.name ?? props.NAME ?? props.state ?? props.STATE
  if (name) {
    const key = String(name).trim().toUpperCase().replace(/\s+/g, '_')
    return stateNameToAbbr[String(name).trim().toUpperCase()] ?? STATE_NAME_TO_ABBR[key] ?? null
  }
  return null
}

const getPopulationRadius = (population: number | null) => {
  if (!population || !Number.isFinite(population)) return 3
  const scaled = Math.sqrt(population) / 150
  return Math.max(3, Math.min(9, scaled))
}

export default function HeatmapMap({
  cities,
  selectedState,
  onStateSelect,
  onCitySelect
}: HeatmapMapProps) {
  const [hoveredState, setHoveredState] = useState<string | null>(null)
  const [focusedState, setFocusedState] = useState<string | null>(null)
  const [hoveredCityId, setHoveredCityId] = useState<number | null>(null)
  const [mapPosition, setMapPosition] = useState<{ coordinates: [number, number]; zoom: number }>({
    coordinates: [-98, 38],
    zoom: 1
  })

  const handleFocusState = (code: string, geo: any) => {
    const centroid = geoCentroid(geo)
    const projection = geoAlbersUsa().translate([MAP_WIDTH / 2, MAP_HEIGHT / 2])
    const path = geoPath(projection)
    const bounds = path.bounds(geo)
    const dx = bounds[1][0] - bounds[0][0]
    const dy = bounds[1][1] - bounds[0][1]
    const scale = dx && dy ? Math.min(MAP_WIDTH / dx, MAP_HEIGHT / dy) * 0.92 : 2.4
    const nextZoom = Math.max(1, Math.min(6, scale))
    setFocusedState(code)
    setMapPosition({
      coordinates: [centroid[0], centroid[1]],
      zoom: nextZoom
    })
  }

  const handleReset = () => {
    setFocusedState(null)
    setMapPosition({ coordinates: [-98, 38], zoom: 1 })
    onStateSelect(null)
  }

  return (
    <div className="relative h-full w-full bg-[var(--map-bg)]">
      {focusedState && (
        <button
          type="button"
          onClick={handleReset}
          className="absolute left-4 top-4 z-10 rounded-full border border-[var(--border)] bg-[var(--surface-1)] px-3 py-1 text-xs font-semibold text-[var(--text-2)] shadow-sm transition hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
        >
          Back to US Map
        </button>
      )}
      <ComposableMap
        projection="geoAlbersUsa"
        width={MAP_WIDTH}
        height={MAP_HEIGHT}
        className="h-full w-full"
      >
        <ZoomableGroup
          center={mapPosition.coordinates}
          zoom={mapPosition.zoom}
          minZoom={1}
          maxZoom={6}
          translateExtent={[
            [0, 0],
            [MAP_WIDTH, MAP_HEIGHT]
          ]}
          onMoveEnd={(position: { coordinates: [number, number]; zoom: number }) =>
            setMapPosition({
              coordinates: [position.coordinates[0], position.coordinates[1]],
              zoom: position.zoom
            })
          }
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }: { geographies: any[] }) =>
              geographies.map((geo) => {
                const code = getStateCode(geo)
                if (focusedState && code !== focusedState) return null
                const isHovered = hoveredState === code
                const isSelected = selectedState === code
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={isSelected ? 'var(--map-state-selected)' : 'var(--map-state)'}
                    stroke={isHovered ? 'var(--map-stroke-hover)' : 'var(--map-stroke)'}
                    strokeWidth={isHovered ? 1.2 : 0.7}
                    onMouseEnter={() => setHoveredState(code ?? null)}
                    onMouseLeave={() => setHoveredState(null)}
                    onClick={() => {
                      if (!code) return
                      const nextState = code === selectedState ? null : code
                      onStateSelect(nextState)
                      if (nextState) {
                        handleFocusState(code, geo)
                      } else {
                        handleReset()
                      }
                    }}
                    style={{
                      default: { outline: 'none' },
                      hover: { fill: 'var(--map-state-hover)', outline: 'none' },
                      pressed: { outline: 'none' }
                    }}
                  />
                )
              })
            }
          </Geographies>
          {cities.map((city) =>
            city.lat != null && city.lng != null ? (
              <Marker
                key={`${city.primary_city}-${city.primary_state}`}
                coordinates={[city.lng, city.lat]}
                onClick={() => onCitySelect(city)}
                onMouseEnter={() => setHoveredCityId(city.id)}
                onMouseLeave={() =>
                  setHoveredCityId((prev) => (prev === city.id ? null : prev))
                }
              >
                <title>{`${city.primary_city}, ${city.primary_state}`}</title>
                <g>
                  <circle
                    r={getPopulationRadius(city.rank_population)}
                    fill="var(--map-point)"
                    opacity={0.82}
                  />
                  {hoveredCityId === city.id && (
                    <text
                      y={-(getPopulationRadius(city.rank_population) + 8)}
                      textAnchor="middle"
                      style={{
                        fontSize: 9,
                        fontWeight: 600,
                        fill: 'var(--map-text)',
                        paintOrder: 'stroke',
                        stroke: 'var(--map-text-stroke)',
                        strokeWidth: 3
                      }}
                    >
                      {city.primary_city}
                    </text>
                  )}
                </g>
              </Marker>
            ) : null
          )}
        </ZoomableGroup>
      </ComposableMap>
    </div>
  )
}
